import { useMemo } from 'react';
import { View, type ViewProps } from 'react-native';
import { Text } from '@/components/ui/text';
import { useTranslation } from 'react-i18next';
import { subDays } from 'date-fns';
import { ConsumedDateRange } from '@/lib/local-api/meal/consumed-date-range';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import { useQuery } from '@apollo/client/react';
import { type Meal, MealsByIdsDocument } from '~/graphql/generated/graphql';
import { Icon } from '~/global/components/icon/Icon';

const FAVORITES_WINDOW_DAYS = 30;
const FAVORITES_COUNT = 3;

interface FavoriteMealView {
  id: string;
  name: string;
  loggedCount: number;
  calories: number;
  macros: string;
}

interface FavoriteMealItemProps {
  meal: FavoriteMealView;
  isLast: boolean;
}

const FavoriteMealItem = ({ meal, isLast }: FavoriteMealItemProps) => {
  const { t } = useTranslation('meal');

  return (
    <View
      className={`flex flex-row items-center gap-3.5 p-4 ${!isLast ? 'border-b border-border' : ''}`}
    >
      <View className="h-11 w-11 rounded-lg bg-medal-gold-soft items-center justify-center">
        <Icon name="Star" size={20} className="text-medal-gold" />
      </View>
      <View className="flex-1 gap-0.5">
        <Text className="font-inter-medium text-sm text-text-primary">
          {meal.name}
        </Text>
        <Text className="font-inter-regular text-xs text-text-muted">
          {t('favorites.loggedTimes', { count: meal.loggedCount })}
        </Text>
      </View>
      <View className="items-end gap-0.5">
        <Text className="font-sora-bold text-sm text-text-primary">
          {meal.calories} cal
        </Text>
        <Text className="font-inter-medium text-[10px] text-text-muted">
          {meal.macros}
        </Text>
      </View>
    </View>
  );
};

export interface FavoriteMealsSectionProps extends ViewProps {}

export const FavoriteMealsSection = ({
  className,
  ...props
}: FavoriteMealsSectionProps) => {
  const { t } = useTranslation('meal');

  const now = new Date();
  const windowStart = subDays(now, FAVORITES_WINDOW_DAYS);
  // The SQL bounds are a deliberate superset (widened a day each side); the
  // exact local-day decision is made in memory by `filterToDays`.
  const { fromDate, toDate } = ConsumedDateRange.forDays(windowStart, now);

  const { data: rawMealLogs = [] } = useLivePowerSyncQuery<{
    id: string;
    mealId: string;
    consumedAt: string;
  }>(db.MealLog.queries.findByDateRange(fromDate, toDate), [fromDate, toDate]);

  // `fromDate`/`toDate` are the memo keys: they change only when the window
  // does, unlike the per-render `now`/`windowStart` Date objects.
  const mealLogs = useMemo(
    () => ConsumedDateRange.filterToDays(rawMealLogs, windowStart, now),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawMealLogs, fromDate, toDate],
  );

  const mealIds = useMemo(
    () => Array.from(new Set(mealLogs.map((l) => l.mealId).filter(Boolean))),
    [mealLogs],
  );

  const { data: mealsData } = useQuery(MealsByIdsDocument, {
    variables: { ids: mealIds as string[] },
    skip: mealIds.length === 0,
  });

  const mealsById = useMemo(() => {
    const map = new Map<string, Meal>();
    (mealsData?.mealsByIds ?? []).forEach((m) => map.set(m.id, m as Meal));
    return map;
  }, [mealsData]);

  const favorites = useMemo((): FavoriteMealView[] => {
    // Dedupe logs by meal, tracking how often and how recently each was logged.
    const byMeal = new Map<string, { count: number; lastConsumedAt: string }>();

    for (const log of mealLogs) {
      if (!log.mealId || !log.consumedAt) continue;
      const existing = byMeal.get(log.mealId);
      if (existing) {
        existing.count += 1;
        if (log.consumedAt > existing.lastConsumedAt) {
          existing.lastConsumedAt = log.consumedAt;
        }
      } else {
        byMeal.set(log.mealId, {
          count: 1,
          lastConsumedAt: log.consumedAt,
        });
      }
    }

    return Array.from(byMeal.entries())
      .sort(([, a], [, b]) => b.lastConsumedAt.localeCompare(a.lastConsumedAt))
      .slice(0, FAVORITES_COUNT)
      .map(([mealId, info]) => {
        const meal = mealsById.get(mealId);
        const nutrition = meal?.nutritionalInfo;
        return {
          id: mealId,
          name: meal?.name ?? t('unnamedMeal'),
          loggedCount: info.count,
          calories: Math.round(nutrition?.energyKcal ?? 0),
          macros: t('macroShort', {
            carbs: Math.round(nutrition?.carbs ?? 0),
            protein: Math.round(nutrition?.protein ?? 0),
            fats: Math.round(nutrition?.fats ?? 0),
          }),
        };
      });
  }, [mealLogs, mealsById, t]);

  const isEmpty = favorites.length === 0;

  return (
    <View className={className} {...props}>
      <View className="flex flex-row items-center mb-3 gap-3">
        <View className="h-0.5 w-6 rounded-sm bg-accent" />
        <Text className="font-inter-bold text-xs tracking-widest text-text-secondary uppercase">
          {t('favorites.title')}
        </Text>
      </View>

      <View className="rounded-xl bg-surface overflow-hidden">
        {isEmpty ? (
          <View className="py-8">
            <Text className="font-inter-regular text-text-muted text-center text-sm">
              {t('favorites.empty')}
            </Text>
          </View>
        ) : (
          favorites.map((meal, index) => (
            <FavoriteMealItem
              key={meal.id}
              meal={meal}
              isLast={index === favorites.length - 1}
            />
          ))
        )}
      </View>
    </View>
  );
};
